import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { db } from 'config/firebase'
import {
  doc,
  DocumentData,
  getDoc,
  updateDoc,
} from 'firebase/firestore'
import isEqual from 'lodash/isEqual'
import { Vehicle } from '@otw/models'
import { ACTION_BANNER_QUERY_ROOT } from './actionBannerQueries'
import { DASHBOARD_QUERY_ROOT } from './fetchMasterInventory'

interface UseInventoryWriteOptions {
  dontShowUpdates?: boolean
}

interface UpdateDocumentParams {
  id: string
  data: Partial<DocumentData>
  isRevert?: boolean
}

const patchVehicleLists = (old: unknown, id: string, data: Partial<DocumentData>) => {
  if (!Array.isArray(old)) return old
  return (old as Vehicle[]).map(item => (item.id === id ? { ...item, ...data } : item))
}

/** Vehicle writes without a collection getDocs or onSnapshot. */
export const useInventoryWrite = (
  inventoryCollection: 'imported_vehicles' | 'master_inventory' | 'shield_inventory' = 'master_inventory',
  options?: UseInventoryWriteOptions,
) => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo, currentUser } = useAuth()
  const queryKey = [inventoryCollection] as const

  const updateDocument = useMutation({
    mutationFn: async ({ id, data, isRevert = false }: UpdateDocumentParams) => {
      const docRef = doc(db, inventoryCollection, id)

      if (isRevert) {
        await updateDoc(docRef, {
          ...data,
          modified_by: userInfo?.email,
          updated_by: currentUser?.uid,
          updated_at: new Date().toISOString(),
        })
        return { id, data }
      }

      const docSnap = await getDoc(docRef)
      const currentData = docSnap.data()

      const actualChanges = Object.entries(data).reduce(
        (acc, [key, value]) => {
          if (value === undefined) return acc
          if (!isEqual(currentData?.[key], value)) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, unknown>,
      )

      if (Object.keys(actualChanges).length > 0) {
        await updateDoc(docRef, {
          ...actualChanges,
          modified_by: userInfo?.email,
          updated_by: currentUser?.uid,
          updated_at: new Date().toISOString(),
        })
      }

      return { id, data: actualChanges }
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousInventory = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, old => patchVehicleLists(old, id, data))
      queryClient.setQueriesData({ queryKey: [DASHBOARD_QUERY_ROOT] }, old =>
        patchVehicleLists(old, id, data),
      )
      return { previousInventory }
    },
    onError: (err, _variables, context) => {
      if (context?.previousInventory) {
        queryClient.setQueryData(queryKey, context.previousInventory)
      }
      if (!options?.dontShowUpdates) {
        showError('Error updating document')
      }
      console.error('Error updating document:', err)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Changes saved successfully')
      }
      void queryClient.invalidateQueries({ queryKey: [ACTION_BANNER_QUERY_ROOT] })
      void queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_ROOT] })
    },
  })

  return {
    updateDocument: updateDocument.mutate,
    updateDocumentAsync: updateDocument.mutateAsync,
  }
}
